import React, { useState } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';

const RESOLUTION_LABELS = {
    0: { text: 'קיים', color: '#388e3c' },
    1: { text: 'לפי שם', color: '#1976d2' },
    2: { text: 'כרטיס חדש', color: '#f57c00' },
    3: { text: 'כרטיס חדש + מזהה חיצוני', color: '#d32f2f' },
};

const RivhitClosingBalanceImportModal = ({
    open,
    onClose,
    previewRivhitClosingBalance,
    commitRivhitClosingBalance,
    direction,
    t
}) => {
    const [trialBalanceFile, setTrialBalanceFile] = useState(null);
    const [palCodeFile, setPalCodeFile] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear() - 1);
    const [previewData, setPreviewData] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);

    const handleTrialBalanceDrop = files => {
        if (files?.length) setTrialBalanceFile(files[0]);
    };

    const handlePalCodeDrop = files => {
        if (files?.length) setPalCodeFile(files[0]);
    };

    const handlePreview = async () => {
        if (!trialBalanceFile || !palCodeFile) return;

        try {
            setProcessing(true);
            const res = await previewRivhitClosingBalance(trialBalanceFile, palCodeFile, year);
            setPreviewData(res);
            setPreviewDone(true);
        } catch (err) {
            console.error('Rivhit closing balance preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCommit = async () => {
        if (!trialBalanceFile || !palCodeFile) return;

        try {
            setProcessing(true);
            const res = await commitRivhitClosingBalance(trialBalanceFile, palCodeFile, year);

            if (res?.errorMessages?.length > 0) {
                console.error('Import errors:', res.errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Rivhit closing balance import error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setTrialBalanceFile(null);
        setPalCodeFile(null);
        setPreviewDone(false);
        setPreviewData(null);
        onClose();
    };

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const getResolutionLabel = (status) => {
        return RESOLUTION_LABELS[status] || { text: '?', color: '#666' };
    };

    if (!open) return null;

    const entries = previewData?.entries ?? [];

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={e => e.stopPropagation()}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            יתרת סגירה - ריוחית (Rivhit)
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <h5>מאזן בוחן (CSV with headers)</h5>
                            <DropzoneArea
                                key={trialBalanceFile ? trialBalanceFile.name : 'trial-empty'}
                                onDrop={handleTrialBalanceDrop}
                                accept=".csv"
                                filesLimit={1}
                            />
                            {trialBalanceFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {trialBalanceFile.name}</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <h5>קובץ סיווג מאזני (CSV with headers)</h5>
                            <DropzoneArea
                                key={palCodeFile ? palCodeFile.name : 'palcode-empty'}
                                onDrop={handlePalCodeDrop}
                                accept=".csv"
                                filesLimit={1}
                            />
                            {palCodeFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {palCodeFile.name}</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <h5>שנה</h5>
                            <TextField
                                type="number"
                                value={year}
                                onChange={e => setYear(parseInt(e.target.value) || new Date().getFullYear() - 1)}
                                size="small"
                                inputProps={{ min: 2000, max: 2100 }}
                                sx={{ width: 150 }}
                            />
                            <span style={{ marginRight: 10, fontSize: '13px', color: '#666' }}>
                                (תאריך מסמך: 31/12/{year})
                            </span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handlePreview}
                            disabled={!trialBalanceFile || !palCodeFile || processing}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing}
                            onClick={handleCommit}
                        >
                            {processing ? t('Processing') : t('Import')}
                        </Button>
                    </div>

                    {previewDone && previewData && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: '14px', color: '#666' }}>
                                    {'שורות שנקראו'}: <strong>{previewData.parsedRows}</strong> |
                                    {' '}{'שורות עם יתרה'}: <strong>{entries.length}</strong> |
                                    {' '}{'כרטיסים חדשים'}: <strong>{previewData.newCardsToCreate}</strong> |
                                    {' '}{'מיפוי מזהה חיצוני'}: <strong>{previewData.cardsWithExternalIdMapping}</strong>
                                </p>
                            </div>

                            {entries.length > 0 && (
                                <div style={{
                                    marginBottom: 20,
                                    padding: '15px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd'
                                }}>
                                    <h5 style={{ marginTop: 0, marginBottom: 15 }}>{'סיכום'}</h5>
                                    <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {'סה"כ חובה'}
                                            </div>
                                            <div style={{
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                color: '#388e3c'
                                            }}>
                                                ₪{formatAmount(previewData.totalDebit)}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {'סה"כ זכות'}
                                            </div>
                                            <div style={{
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                color: '#d32f2f'
                                            }}>
                                                ₪{formatAmount(previewData.totalCredit)}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {'הפרש'}
                                            </div>
                                            <div style={{
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                color: previewData.balance < 0 ? '#d32f2f' : previewData.balance > 0 ? '#388e3c' : '#666'
                                            }}>
                                                ₪{formatAmount(previewData.balance)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {entries.length === 0 ? (
                                <p style={{ color: '#666' }}>{'לא נמצאו יתרות'}</p>
                            ) : (
                                <>
                                    <h5>{'יתרות סגירה'}</h5>
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'מזהה לקוח'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'שם'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'קוד סיווג מאזני'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'שם סיווג מאזני'}</th>
                                                <th style={{ padding: '10px', textAlign: 'right', border: '1px solid #ddd' }}>{'סכום'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'חו/ז'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'סטטוס כרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{'כרטיס'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {entries.map((entry, i) => {
                                                const resolution = getResolutionLabel(entry.cardResolutionStatus);
                                                const isCredit = entry.sortCode === '2';

                                                return (
                                                    <tr key={i} style={{
                                                        backgroundColor: entry.cardResolutionStatus >= 2
                                                            ? '#fff8e1'
                                                            : i % 2 === 0 ? '#fff' : '#f9f9f9'
                                                    }}>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.custId}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.custName}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.palCode}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.palCodeName || ''}</td>
                                                        <td style={{
                                                            padding: '10px',
                                                            border: '1px solid #ddd',
                                                            textAlign: 'right',
                                                            color: isCredit ? '#d32f2f' : 'inherit',
                                                            fontWeight: isCredit ? 'bold' : 'normal'
                                                        }}>
                                                            {formatAmount(entry.amount)}
                                                        </td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {entry.sortCode === '1' ? 'חובה' : 'זכות'}
                                                        </td>
                                                        <td style={{
                                                            padding: '10px',
                                                            border: '1px solid #ddd',
                                                            textAlign: direction === 'rtl' ? 'right' : 'left',
                                                            color: resolution.color,
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {resolution.text}
                                                        </td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                            {entry.resolvedCardId || '-'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RivhitClosingBalanceImportModal;
